import { Category } from '@Client/runner.directives/common/Category/Category';
import EmptyState from '@Client/runner.directives/common/EmptyState';
import { RunnerPageDescription } from '@Client/runner.directives/common/PageDescription';
import { getService } from '@Client/runner.hooks/useService';
import { ITreeCategory } from '@Client/runner.services/runner.category-navigation.service';
import Search from '@Shared.Angular/flowingly.components/Search/Search';
import React, { useEffect, useMemo, useState } from 'react';
import IProcedureSummary from '@Shared.Angular/@types/core/contracts/queryModel/procedure/procedureSummary';
import ProcedureSummary from './ProcedureSummary';
import { EmptyGuid, Guid } from '@Shared.Angular/@types/guid';
import { FlowinglyPermissions } from '@Shared.Angular/flowingly.services/flowingly.constants';
import { TagFilter } from '@Shared.Angular/flowingly.components/TagFilter/TagFilter';
import {
  CustomTabs,
  ICustomTab
} from '@Shared.Angular/flowingly.components/CustomTabs/CustomTabs';
import { ProcedureViewMode } from '@Shared.Angular/@types/core/contracts/constants/procedureViewMode';
import { STATE_MAPPINGS } from '@Shared.Angular/@types/stateMappings';
import { naturalSortComparator } from '@Shared.Angular/flowingly.helper/naturalSort';

type Props = {
  backFromState?: string;
};

enum TabId {
  ALL = 'all',
  RECENT = 'recent'
}

export const Procedures = (props: Props) => {
  const { backFromState } = props;
  const permissionsService = getService('permissionsService');
  const sessionService = getService('sessionService');
  const $state = getService('$state');
  const proceduresService = getService('proceduresService');
  const pubsubService = getService('pubsubService');
  const runnerStartFlowsFormatter = getService('runnerStartFlowsFormatter');
  const runnerCategoryNavigationService = getService(
    'runnerCategoryNavigationService'
  );
  const APP_CONFIG = getService('APP_CONFIG');
  const brandingService = getService('brandingService');

  const allCategoriesId = EmptyGuid;

  const [categories, setCategories] = useState<
    ITreeCategory<IProcedureSummary>[]
  >([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>(
    backFromState === STATE_MAPPINGS.procedure
      ? proceduresService.selectedCategoryId
      : allCategoriesId
  );
  const [allProcedures, setAllProcedures] = useState<IProcedureSummary[]>([]);
  const [displayedProcedures, setDisplayedProcedures] = useState<
    IProcedureSummary[]
  >([]);
  const [isDataReady, setIsDataReady] = useState(false);
  const [lowerCaseSearchText, setLowerCaseSearchText] = useState('');
  const [usedTagIds, setUsedTagIds] = useState<Guid[]>([]);
  const [filterTagIds, setFilterTagIds] = useState<Guid[]>([]);
  const canCreateSop = permissionsService.currentUserHasPermission(
    FlowinglyPermissions.PROCEDURE_CREATE
  );
  const shouldShowDemo =
    brandingService.usingDefaultBrandName() && !!APP_CONFIG.procedureDemoUrl;

  const summaryText = shouldShowDemo
    ? canCreateSop
      ? 'You have no SOPs'
      : 'View SOPs'
    : 'Hi ' + sessionService.getUser().fullName.split(' ')[0];

  const buttonText = canCreateSop ? 'Create a new SOP' : '';

  const handleButtonClick = canCreateSop
    ? () => $state.go(STATE_MAPPINGS.libraryprocedures)
    : undefined;

  const demoUrl = shouldShowDemo ? APP_CONFIG.procedureDemoUrl : undefined;

  const [selectedTabId, setSelectedTabId] = useState(TabId.ALL);
  const customTabs: ICustomTab[] = useMemo(
    () => [
      { id: TabId.ALL, label: 'All SOPs' },
      {
        id: TabId.RECENT,
        label: 'Recently Updated',
        title:
          'SOPs updated in the last ' +
          APP_CONFIG.maxNumberOfDaysForRecentlyUpdated +
          ' days'
      }
    ],
    []
  );
  const updateSelectedTabId = (newTabId: string) => {
    setSelectedTabId(newTabId as TabId);
    updateDisplayedProcedures(
      allProcedures,
      lowerCaseSearchText,
      selectedCategoryId,
      filterTagIds,
      newTabId as TabId
    );
  };

  useEffect(() => {
    const subscriberId = 'proceduresPage';

    proceduresService.getProcedures().then(setNewProcedures);
    pubsubService.subscribeManyLate(
      ['PROCEDURE_UPDATED', 'PROCEDURE_DELETED', 'PROCEDURE_PUBLISHED'],
      () => proceduresService.getProcedures().then(setNewProceduresRef.current),
      subscriberId
    );

    return () => {
      pubsubService.unsubscribeAll(subscriberId);
    };
  }, []);

  const setNewProceduresRef = React.useRef<
    (procedures: IProcedureSummary[]) => void
  >(() => {
    return;
  });
  const setNewProcedures = (procedures: IProcedureSummary[]) => {
    procedures.sort((leftProcedure, rightProcedure) =>
      naturalSortComparator(leftProcedure.name, rightProcedure.name)
    );
    setAllProcedures(procedures);
    const newUsedTagIds = procedures.flatMap((p) => p.tags).map((t) => t.id);
    setUsedTagIds(newUsedTagIds);
    const validFilterTagIds = filterTagIds.filter((t) =>
      newUsedTagIds.includes(t)
    );
    setFilterTagIds(validFilterTagIds);
    updateDisplayedProcedures(
      procedures,
      lowerCaseSearchText,
      selectedCategoryId,
      filterTagIds,
      selectedTabId
    );

    const newCategories = generateCategories(procedures);
    setCategories(newCategories);
    const findSelectedCategory = (current: ITreeCategory<unknown>) => {
      if (current.id === selectedCategoryId) {
        return true;
      }
      return current.items.some(findSelectedCategory);
    };
    if (!newCategories.some(findSelectedCategory)) {
      setSelectedCategoryId(allCategoriesId);
      proceduresService.selectedCategoryId = allCategoriesId;
    }

    setIsDataReady(true);
  };
  useEffect(() => {
    setNewProceduresRef.current = setNewProcedures;
  }, [setNewProcedures]);

  const updateDisplayedProcedures = (
    procedures: IProcedureSummary[],
    searchText: string,
    selectedCategoryId: string,
    filterTagIds: Guid[],
    selectedTab: TabId
  ) => {
    let filteredProcedures = procedures;

    if (searchText) {
      filteredProcedures = procedures.filter(
        (procedure) =>
          procedure.name.toLowerCase().includes(searchText) ||
          procedure.description?.toLowerCase().includes(searchText)
      );
    }

    if (selectedCategoryId && selectedCategoryId !== allCategoriesId) {
      filteredProcedures = filteredProcedures.filter(
        (procedure) => procedure.categoryId === selectedCategoryId
      );
    }

    if (filterTagIds.length > 0) {
      filteredProcedures = filteredProcedures.filter((procedure) =>
        procedure.tags.some((tag) => filterTagIds.includes(tag.id))
      );
    }

    if (selectedTab === TabId.RECENT) {
      const cutoffDate = new Date().setDate(
        new Date().getDate() - APP_CONFIG.maxNumberOfDaysForRecentlyUpdated
      );
      const cutoffDateString = new Date(cutoffDate).toISOString().split('Z')[0];
      filteredProcedures = filteredProcedures.filter(
        (procedure) => procedure.updatedUtc >= cutoffDateString
      );
      filteredProcedures.sort((a, b) =>
        b.updatedUtc.localeCompare(a.updatedUtc, undefined, {
          numeric: true,
          sensitivity: 'base'
        })
      );
    }

    setDisplayedProcedures(filteredProcedures);
  };

  const updateSearchText = (newText: string) => {
    const lowerCaseNewText = newText.toLowerCase();
    setLowerCaseSearchText(lowerCaseNewText);
    updateDisplayedProcedures(
      allProcedures,
      lowerCaseNewText,
      selectedCategoryId,
      filterTagIds,
      selectedTabId
    );
  };

  const selectCategory = (newCategoryId: Guid) => {
    setSelectedCategoryId(newCategoryId);
    proceduresService.selectedCategoryId = newCategoryId;
    updateDisplayedProcedures(
      allProcedures,
      lowerCaseSearchText,
      newCategoryId,
      filterTagIds,
      selectedTabId
    );
  };

  const generateCategories = (procedures: IProcedureSummary[]) => {
    const proceduresByCategory =
      runnerStartFlowsFormatter.groupObjectsByCategory(procedures);
    const newCategories =
      runnerCategoryNavigationService.convertToTreeViewCompatibleCategories(
        proceduresByCategory
      );

    newCategories.sort((leftCategory, rightCategory) =>
      naturalSortComparator(leftCategory.text, rightCategory.text)
    );

    newCategories.splice(0, 0, {
      id: allCategoriesId,
      text: 'All',
      flowModels: [],
      items: []
    });
    return newCategories;
  };

  const changeTagFilter = (newTagIds: Guid[]) => {
    setFilterTagIds(newTagIds);
    updateDisplayedProcedures(
      allProcedures,
      lowerCaseSearchText,
      selectedCategoryId,
      newTagIds,
      selectedTabId
    );
  };

  const procedureClicked = (procedureId: string) => {
    $state.go(STATE_MAPPINGS.procedure, {
      procedureId: procedureId,
      viewMode: ProcedureViewMode.ReadOnly
    });
  };

  return (
    <div id="procedures" className="runner-page">
      <div className="mobile-hide">
        <RunnerPageDescription
          pageHeaderText="Standard Operating Procedures"
          pageDescription="A list of Standard Operating Procedures published to you."
          helpLink="https://help.flowingly.net/en/articles/12388703-how-to-set-up-start-using-ai-sops-in-flowingly"
        />
      </div>
      {isDataReady && allProcedures.length === 0 ? (
        <EmptyState
          summaryText={summaryText}
          bodyText="You have no SOPs published to you."
          buttonText={buttonText}
          onButtonClick={handleButtonClick}
          demoUrl={demoUrl}
        />
      ) : (
        isDataReady && (
          <div className="procedures-content runner-page__content">
            <Category
              categorisedItems={categories}
              selectedCategoryId={selectedCategoryId}
              onCategoryClick={selectCategory}
              defaultItemId={allCategoriesId}
            />
            <div className="procedures-list-container runner-page__content__main-wrapper">
              <div className="procedures-list runner-page__content__main-content">
                <div className="toolbar">
                  <Search
                    id="procedure-search"
                    value={lowerCaseSearchText}
                    onValueChange={updateSearchText}
                    placeholder="Search by SOP name or description"
                  />
                  <div className="filter-container mobile-hide">
                    <CustomTabs
                      tabItems={customTabs}
                      selectedTabId={selectedTabId}
                      onChange={updateSelectedTabId}
                    />
                    <TagFilter
                      usedTagIds={usedTagIds}
                      onApply={changeTagFilter}
                    />
                  </div>
                </div>
                {displayedProcedures.map((procedure) => (
                  <ProcedureSummary
                    key={procedure.id}
                    procedure={procedure}
                    onClick={procedureClicked}
                  />
                ))}
                {displayedProcedures.length === 0 && (
                  <EmptyState
                    summaryText="No matching SOPs found"
                    bodyText="Try adjusting your search or filter criteria."
                    buttonText=""
                  />
                )}
              </div>
            </div>
          </div>
        )
      )}
    </div>
  );
};
